import { useEffect, useState } from 'react';
import { LsLineItem } from '@/components/ls-line-item/LsLineItem';
import { LsOrderForm, type OrderFormValues } from '@/components/ls-order-form/LsOrderForm';
import { useCartStore } from '@/features/cart/cart.store';
import { fixHangers } from '@/shared/lib/typography';
import { openPayment, readOrder, startCheckout, type Checkout } from '@mini/features/checkout/checkout.api';
import { haptic, openExternal } from '@mini/platform/telegram';
import styles from './pay-screen.module.scss';

interface Props {
  partnerSlug: string;
}

/**
 * Заказ и оплата.
 *
 * Три состояния, и все три видны человеку без догадок: он заполняет форму,
 * он платит, он видит, что заказ принят.
 *
 * Заказ создаётся до оплаты. Если карта не пройдёт, у нас останутся
 * и состав, и контакты, и собранный макет — потерять всё это из-за отказа
 * банка хуже, чем подержать неоплаченный заказ в базе.
 *
 * Оплачен заказ или нет, решает сервер по ответу провайдера. Возвращение
 * человека в приложение не значит ничего: закрыть страницу оплаты и
 * вернуться можно, не заплатив ни рубля.
 */
export function PayScreen({ partnerSlug }: Props) {
  const items = useCartStore((s) => s.items);
  const setQty = useCartStore((s) => s.setQty);
  const remove = useCartStore((s) => s.remove);
  const clear = useCartStore((s) => s.clear);

  const [checkout, setCheckout] = useState<Checkout | null>(null);
  const [status, setStatus] = useState<string>('new');
  const [isBusy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const confirm = async (values: OrderFormValues) => {
    setBusy(true);
    setError(null);

    try {
      const created = await startCheckout(partnerSlug, items, values);
      setCheckout(created);

      const payUrl = await openPayment(created.token);

      // Корзину чистим только после того, как заказ уже на сервере:
      // упавший запрос не должен стоить человеку собранной вещи.
      clear();
      haptic('ok');

      // Внутренним браузером телеграма: человек остаётся в клиенте
      // и возвращается сюда одним нажатием.
      openExternal(payUrl);
    } catch {
      haptic('error');
      setError('Не получилось оформить заказ. Попробуйте ещё раз.');
    } finally {
      setBusy(false);
    }
  };

  /**
   * Открыть страницу оплаты ещё раз.
   *
   * Отдельно от первого нажатия: там заказ ещё создаётся, здесь он уже
   * есть. Общая на оба случая обработка ошибки сказала бы «не получилось
   * оформить заказ» человеку, у которого заказ оформлен, — и он начал бы
   * собирать вещь заново.
   */
  const reopen = async () => {
    setBusy(true);
    setError(null);

    try {
      openExternal(await openPayment(checkout?.token ?? ''));
    } catch {
      haptic('error');

      // Заказ мог быть оплачен в соседнем окне, пока человек смотрел
      // на эту кнопку. Перечитываем заказ: если оплачен — экран сменится
      // сам, и никакой ошибки показывать не нужно.
      const order = checkout ? await readOrder(checkout.token).catch(() => null) : null;

      if (order?.paymentStatus === 'paid') {
        setStatus('paid');
      } else {
        setError('Страница оплаты не открылась. Попробуйте ещё раз или напишите нам.');
      }
    } finally {
      setBusy(false);
    }
  };

  // Человек вернулся со страницы оплаты — перечитываем заказ. Телеграм
  // не сообщает о возврате, поэтому ловим момент, когда окно снова стало
  // видимым: это и есть возвращение.
  useEffect(() => {
    if (!checkout) return;

    const refresh = (): void => {
      if (document.visibilityState !== 'visible') return;
      void readOrder(checkout.token)
        .then((order) => setStatus(order.paymentStatus))
        .catch(() => undefined);
    };

    refresh();
    document.addEventListener('visibilitychange', refresh);
    return () => document.removeEventListener('visibilitychange', refresh);
  }, [checkout]);

  if (checkout) {
    const isPaid = status === 'paid';

    return (
      <section className={styles['ls-pay']}>
        <p className={styles['ls-pay__number']}>Заказ №{checkout.orderNumber}</p>

        <h1 className={styles['ls-pay__title']}>
          {fixHangers(isPaid ? 'Оплачено. Заказ в работе!' : 'Заказ создан. Ждём оплату')}
        </h1>

        <p className={styles['ls-pay__lead']}>
          {fixHangers(
            isPaid
              ? 'Мы напишем в этот чат, когда отправим посылку.'
              : 'Страница оплаты открылась в отдельном окне. Если закрыли её случайно — откройте снова.',
          )}
        </p>

        {!isPaid && (
          <>
            {/*
              Отказ здесь обязан быть виден. Раньше кнопка просто
              не делала ничего: провайдер ответил отказом, ссылка
              протухла или заказ уже оплачен — человек жал ещё раз
              и ещё раз, а на экране не менялось ничего.
            */}
            <button
              className={styles['ls-pay__again']}
              type="button"
              disabled={isBusy}
              onClick={() => void reopen()}
            >
              Открыть оплату
            </button>

            {error && <p className={styles['ls-pay__error']}>{error}</p>}
          </>
        )}
      </section>
    );
  }

  return (
    <section className={styles['ls-pay']}>
      <h1 className={styles['ls-pay__title']}>{fixHangers('Куда доставить заказ?')}</h1>

      <ul className={styles['ls-pay__items']}>
        {items.map((item) => (
          <LsLineItem
            key={item.id}
            item={item}
            onQtyChange={(qty) => setQty(item.id, qty)}
            onRemove={() => remove(item.id)}
          />
        ))}
      </ul>

      {error && <p className={styles['ls-pay__error']}>{error}</p>}

      <LsOrderForm isSubmitting={isBusy} onSubmit={(values) => void confirm(values)} />
    </section>
  );
}
